/**
 * TrialBanner — a slim top-of-app strip that surfaces the tenant's billing
 * state (trial / grace / past_due / locked). Auto-refreshes every 5 minutes
 * so a countdown reflects real time.
 *
 * States:
 *   - active  → nothing rendered (paid customer, don't nag)
 *   - trial   → subtle info bar with "X days left in trial" + Subscribe CTA
 *   - grace   → warning bar with "Trial ended, X days grace" + Subscribe CTA
 *   - past_due / locked → destructive bar with "Access will end / has ended"
 *
 * The Subscribe button navigates to Settings → Subscription. If billing isn't
 * configured on the server (no Razorpay creds), the CTA is hidden.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

const REFRESH_MS = 5 * 60 * 1000; // 5 min

export function TrialBanner() {
    const [state, setState] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        let mounted = true;
        async function refresh() {
            try {
                const s = await api.getBillingStatus();
                if (!mounted) return;
                setState(s || null);
            } catch { /* ignore transient failures */ }
            finally { if (mounted) setLoaded(true); }
        }
        refresh();
        const t = setInterval(refresh, REFRESH_MS);
        return () => { mounted = false; clearInterval(t); };
    }, []);

    if (!loaded || !state) return null;
    const kind = state.state || 'unknown';

    // Nothing to say for paid/active customers.
    if (kind === 'active' || kind === 'unknown') return null;

    const goSubscribe = () => nav('/settings#billing');

    const cfgOk = !!state.configured;
    const days = state.days_left;

    // Copy per state
    let bg, fg, border, icon, title, body;
    if (kind === 'trial') {
        bg = 'bg-[hsl(var(--info-bg,204_100%_97%))]';
        fg = 'text-[hsl(var(--info-fg,204_80%_25%))]';
        border = 'border-[hsl(var(--info-border,204_90%_85%))]';
        icon = <Clock size={14} className="mt-0.5 flex-none" />;
        title = 'Free trial';
        body = (
            <>
                {days == null
                    ? <>You're on the 30-day free trial.</>
                    : days > 1
                        ? <><strong className="tabular-nums">{days} days</strong> left in your free trial.</>
                        : <><strong>Last day</strong> of your free trial — subscribe today to keep access.</>}
            </>
        );
    } else if (kind === 'grace') {
        bg = 'bg-[hsl(var(--warning-bg))]';
        fg = 'text-[hsl(var(--warning-fg))]';
        border = 'border-[hsl(var(--warning-border))]';
        icon = <AlertTriangle size={14} className="mt-0.5 flex-none" />;
        title = 'Trial ended — grace period';
        body = (
            <>
                Your free trial has ended.{' '}
                {days == null
                    ? <>You have a short grace window to subscribe.</>
                    : <><strong className="tabular-nums">{days} days</strong> of full access remain. Subscribe now to avoid a lock.</>}
            </>
        );
    } else if (kind === 'past_due') {
        bg = 'bg-[hsl(var(--destructive-bg,0_100%_97%))]';
        fg = 'text-[hsl(var(--destructive-fg,0_75%_35%))]';
        border = 'border-[hsl(var(--destructive-border,0_90%_85%))]';
        icon = <AlertTriangle size={14} className="mt-0.5 flex-none" />;
        title = 'Payment failed';
        body = <>We couldn't collect your subscription payment. Please update your payment method to keep uploading bills.</>;
    } else if (kind === 'locked') {
        bg = 'bg-[hsl(var(--destructive-bg,0_100%_97%))]';
        fg = 'text-[hsl(var(--destructive-fg,0_75%_35%))]';
        border = 'border-[hsl(var(--destructive-border,0_90%_85%))]';
        icon = <AlertTriangle size={14} className="mt-0.5 flex-none" />;
        title = 'Access locked';
        body = <>Your grace period is over. Subscribe to resume bill uploads. Your existing data is safe.</>;
    } else {
        return null;
    }

    return (
        <div
            className={`px-4 sm:px-6 py-2 text-xs sm:text-sm border-b ${bg} ${fg} ${border} flex items-start gap-3`}
            data-testid={`trial-banner-${kind}`}
        >
            {icon}
            <div className="flex-1 leading-relaxed">
                <span className="font-semibold mr-1.5">{title}:</span>
                <span>{body}</span>
            </div>
            {cfgOk && (
                <Button
                    size="sm"
                    variant={kind === 'trial' ? 'outline' : 'default'}
                    onClick={goSubscribe}
                    className="ml-2 shrink-0"
                    data-testid="trial-banner-subscribe"
                >
                    Subscribe <ArrowRight size={14} className="ml-1" />
                </Button>
            )}
        </div>
    );
}
